/**
 * Categorical color palette — the single source of truth for series/slice/marker
 * identity across every surface (SPA charts + map, Slack rasterizer, map dots).
 *
 * Six fixed hues in a fixed order, validated for CVD safety + contrast. The order
 * IS the safety mechanism, so it never changes. Colors are assigned in sequence
 * and never cycled: a 7th+ series folds into the neutral "Other" swatch rather
 * than reusing (or generating) a hue. Light and dark are the same hues stepped
 * for their surface, not two different palettes.
 *
 * Categorical is used when a chart carries >= 2 series/slices, or a map supplies a
 * legend/group. A single series (one bar set, one gauge, an ungrouped location
 * map) uses slot 1 (brand purple) — there is no gray "neutral" default.
 *
 * Status colors (online/degraded/offline) are a SEPARATE, reserved palette
 * (see `map-colors.ts`) and are never drawn from here — the status green/orange
 * are deliberately distinct hues from categorical slots 2 and 6.
 */

/** Slot 1..6, light surface. */
export const CATEGORICAL_LIGHT: string[] = [
  '#5F26FC', // 1 brand purple
  '#008300', // 2 green
  '#e87ba4', // 3 magenta
  '#eda100', // 4 yellow
  '#1baf7a', // 5 aqua
  '#eb6834', // 6 orange
];

/** Slot 1..6, dark surface — same six hues, stepped for the dark basemap/panel. */
export const CATEGORICAL_DARK: string[] = [
  '#8f6ff0', // 1 brand purple
  '#008300', // 2 green
  '#d55181', // 3 magenta
  '#c98500', // 4 yellow
  '#199e70', // 5 aqua
  '#d95926', // 6 orange
];

/**
 * Neutral "Other" swatch for the 7th+ series (fold, never cycle). A mid gray,
 * deliberately distinct from the reserved offline-status gray (`#7D7D7D`) so an
 * "Other" bucket never reads as a status.
 */
export const CATEGORICAL_OTHER_LIGHT = '#9AA0A6';
export const CATEGORICAL_OTHER_DARK = '#8B8F99';

/** Light/dark palette selector for a chart/map surface. */
export type CategoricalMode = 'light' | 'dark';

/**
 * Concrete hex for categorical slot `index` on `mode`'s surface. Slots 0..5 map
 * to the six fixed hues in order; index 6+ (or a negative index) folds to the
 * "Other" gray — assignment never cycles back to slot 1.
 */
export function categoricalColor(index: number, mode: CategoricalMode = 'light'): string {
  const palette = mode === 'dark' ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
  const other = mode === 'dark' ? CATEGORICAL_OTHER_DARK : CATEGORICAL_OTHER_LIGHT;
  return Number.isInteger(index) && index >= 0 && index < palette.length ? palette[index] : other;
}
